import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  DialogActions,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  Collapse,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import DeleteIcon from "@mui/icons-material/Delete";
import { RemoveCircle as RemoveCircleIcon } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";
import LoadingSpinner from "../common/loading";
import {
  getRoomList,
  getRoom,
  saveRoom,
  deleteRoom,
  getDeviceList,
} from "./room-service.js";
import { useRecoilState } from "recoil";
import { userInfoState } from "../global-state";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import { useHistory } from "react-router-dom";

import { Select } from "antd";

const Room = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery("(max-width:768px)");
  const [roomList, setRoomList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [newRoomName, setNewRoomName] = useState("");
  const [editedRoomName, setEditedRoomName] = useState("");
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const { successToast, warningToast, errorToast } = useToast();
  const [openRoomId, setOpenRoomId] = useState(null);
  const [editRoomId, setEditRoomId] = useState(null);
  const history = useHistory();

  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const { apiCall } = useApiCall();
  const [error, setError] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);

  const [roomDeviceIds, setRoomDeviceIds] = useState(null);
  const [isOccupy, setIsOccupy] = useState("");

  useEffect(() => {
    handleRoomAndDevice();
  }, []);

  const handleRoomAndDevice = async () => {
    setLoading(true);
    await Promise.all([handleRoomList(), handleDeviceList()]);
    setLoading(false);
  };

  const handleDeviceList = async () => {
    try {
      const resp = await getDeviceList(apiCall, userInfo.activeScope.id);
      if (resp) {
        setDeviceList(resp);
      }
    } catch (error) {
      console.error("Error fetching device info:", error);
    }
  };

  const handleRoomList = async () => {
    const resp = await getRoomList(apiCall, userInfo.activeScope.id);
    if (resp) {
      setRoomList(resp.rooms);
    }
  };

  const handleRoomInfo = async (roomId) => {
    const resp = await getRoom(apiCall, userInfo.activeScope.id, roomId);
    if (resp) {
      if (resp.devices) {
        setRoomDeviceIds(new Set(resp.devices.map((device) => device.id)));
      }
    } else {
      errorToast("Error!");
    }
  };

  const handleAddRoomOpen = () => {
    if (isOccupy == "") {
      setIsOccupy("add room");
      setOpenAddDialog(true);
    }
  };

  const handleAddRoomClose = () => {
    setOpenAddDialog(false);
    setIsOccupy("");
  };

  const handleCheckRoomName = (e) => {
    const name = e.target.value;
    if (name != null) {
      setNewRoomName(name);
      if (roomList) {
        if (
          roomList.some((room) => {
            return (
              name.trim().toLowerCase() === room.roomName.trim().toLowerCase()
            );
          })
        ) {
          setError("Room name already exists.");
        } else {
          setError("");
        }
      }
    } else {
      return;
    }
  };

  const handleSaveRoom = async (room) => {
    const resp = await saveRoom(apiCall, room);
    if (resp) {
      successToast("saved");
    } else {
      errorToast("error");
    }
  };

  const handleAddRoom = async () => {
    const room = {
      scopeId: userInfo.activeScope.id,
      roomId: null,
      roomName: newRoomName,
      devices: [],
    };
    await handleSaveRoom(room);
    await handleRoomList();
    handleAddRoomClose();
    setNewRoomName("");
  };

  const handleEditChange = (value, name) => {
    if (value.trim() == "") {
      setError("Room name cannot be empty");
    } else if (roomList) {
      if (
        roomList.some((room) => {
          if (room.roomName !== name)
            return (
              value.trim().toLowerCase() === room.roomName.trim().toLowerCase()
            );
        })
      ) {
        setError("Room name exists.");
      } else {
        setError("");
      }
    }
    setEditedRoomName(value);
  };

  const handleEditRoom = async (room) => {
    setEditRoomId((prevEditRoomId) => {
      return prevEditRoomId === room.roomId ? null : room.roomId;
    });

    if (room.roomName !== editedRoomName) {
      room.roomName = editedRoomName;
      await handleSaveRoom(room);
      await handleRoomList();
    }

    setEditedRoomName("");
  };

  const handleDeleteRoom = async (roomId) => {
    const resp = await deleteRoom(apiCall, userInfo.activeScope.id, roomId);
    if (resp) {
      successToast("deleted");
      handleRoomList();
    } else {
      errorToast("error");
    }
  };

  const handleOpenToggle = (roomId) => {
    setOpenRoomId((prevOpenRoomId) => {
      return prevOpenRoomId === roomId ? null : roomId;
    });
  };

  const handleEditRoomToggle = (roomId, name) => {
    setEditRoomId((prevEditRoomId) => {
      return prevEditRoomId === roomId ? null : roomId;
    });
    setEditedRoomName(name);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleDeleteDevice = async (room, deviceId) => {
    const updateRoom = {
      ...room,
      devices: room.devices?.filter((device) => device.id !== deviceId),
    };

    await handleSaveRoom(updateRoom);
    await handleRoomList();
    await handleRoomInfo(room.roomId);
  };

  const handleAddDevice = async (room) => {
    if (selectedDevice) {
      setIsOccupy("add device");
      const isExist = room.devices.some((device) => {
        device.id === selectedDevice.id;
      });
      if (isExist) {
        errorToast("error!");
        setIsOccupy("");
        return;
      }
      const deviceToAdd = {
        roomId: room.roomId,
        id: selectedDevice.id,
        name: selectedDevice.name,
        integration: selectedDevice.integration,
      };
      room.devices.push(deviceToAdd);
      await handleSaveRoom(room);
      await handleRoomList();
      await handleRoomInfo(room.roomId);
      setSelectedDevice(null);
      setIsOccupy("");
    }
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Box
          display="flex"
          justifyContent="start"
          alignItems="start"
          flexDirection="column"
          height="100%"
          p={2}
          width="100%"
        >
          <Box
            position="static"
            sx={{
              display: "flex",
              alignItems: "center",
              justfyContent: "flex-start",
              mb: 2,
            }}
          >
            {isSmallScreen && (
              <IconButton
                color="inherit"
                edge="start"
                sx={{ mr: 1 }}
                onClick={() => {
                  history.replace("/setting");
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ display: "flex" }}>
              <Typography variant="h6" style={{ textAlign: "center" }}>
                Room
              </Typography>
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap={1} width="100%">
            <Card
              sx={{
                width: "100%",
                backgroundColor: "background.paper",
                borderRadius: "8px",
                boxShadow: "none",
              }}
            >
              <CardContent>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      Rooms
                    </Typography>
                    <Tooltip title="Add new room">
                      <IconButton onClick={handleAddRoomOpen} color="primary">
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {roomList.length === 0 ? (
                    <Typography
                      sx={{
                        height: "80px",
                        lineHeight: "80px",
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      No Rooms, Please Create One
                    </Typography>
                  ) : (
                    roomList
                      .sort((a, b) =>
                        (a.roomName || "").localeCompare(b.roomName || "")
                      )
                      .map((room) => (
                        <Box key={room.roomId} sx={{ mt: 1 }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            {editRoomId == room.roomId ? (
                              <Box sx={{ mr: 1, width: "100%" }}>
                                <TextField
                                  fullWidth
                                  value={editedRoomName}
                                  onChange={(e) =>
                                    handleEditChange(
                                      e.target.value,
                                      room.roomName
                                    )
                                  }
                                  label="Room Name"
                                  variant="outlined"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "4px",
                                      "& input": {
                                        p: 1.2,
                                      },
                                    },
                                    "& .MuiInputLabel-root": {
                                      lineHeight: "0.9",
                                    },
                                  }}
                                  error={!!error}
                                  helperText={error}
                                />
                              </Box>
                            ) : (
                              <Typography>{room.roomName}</Typography>
                            )}

                            <Box sx={{ display: "flex" }}>
                              {isEditing && (
                                <Box sx={{ display: "flex" }}>
                                  <Button
                                    variant="contained"
                                    onClick={() => {
                                      if (editRoomId == room.roomId) {
                                        handleEditRoom(room);
                                        setIsOccupy("");
                                      } else {
                                        setIsOccupy("edit room");
                                        handleEditRoomToggle(
                                          room.roomId,
                                          room.roomName
                                        );
                                      }
                                    }}
                                    disabled={
                                      (editRoomId === room.roomId && !!error) ||
                                      (editRoomId !== room.roomId &&
                                        isOccupy !== "") ||
                                      (isOccupy !== "edit room" &&
                                        isOccupy !== "")
                                    }
                                  >
                                    {editRoomId == room.roomId
                                      ? "Save"
                                      : "Edit"}
                                  </Button>
                                  <IconButton
                                    onClick={() =>
                                      handleDeleteRoom(room.roomId)
                                    }
                                    color="error"
                                  >
                                    <RemoveCircleIcon />
                                  </IconButton>
                                </Box>
                              )}
                              <Box>
                                <IconButton
                                  onClick={() => {
                                    handleOpenToggle(room.roomId);
                                    if (openRoomId !== room.roomId) {
                                      handleRoomInfo(room.roomId);
                                    }
                                  }}
                                >
                                  {openRoomId === room.roomId ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>

                          <Collapse in={openRoomId === room.roomId}>
                            <Divider sx={{ mb: 0.5 }} />

                            {room.devices?.map((device) => (
                              <Box
                                key={device.id}
                                sx={{
                                  mt: 1,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography>{device.name}</Typography>
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    handleDeleteDevice(room, device.id)
                                  }
                                >
                                  <RemoveCircleIcon />
                                </IconButton>
                              </Box>
                            ))}

                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ mt: 2 }}
                            >
                              <Box sx={{ mr: 1, width: "100%" }}>
                                <Autocomplete
                                  ListboxProps={{
                                    style: { maxHeight: "200px" },
                                  }}
                                  options={deviceList}
                                  getOptionLabel={(option) => option.name}
                                  isOptionEqualToValue={(option, value) =>
                                    option.id === value?.id
                                  }
                                  onChange={(event, newValue) =>
                                    setSelectedDevice(newValue)
                                  }
                                  value={selectedDevice}
                                  renderOption={(props, option) => {
                                    const isDisabled = roomDeviceIds
                                      ? roomDeviceIds.has(option.id)
                                      : false;
                                    const { key, ...tagProps } = props;
                                    return (
                                      <li
                                        {...tagProps}
                                        key={key}
                                        style={{
                                          pointerEvents: isDisabled
                                            ? "none"
                                            : "auto",
                                          color: isDisabled ? "gray" : "black",
                                        }}
                                      >
                                        <Typography variant="body1">
                                          {option.name}{" "}
                                          {isDisabled && "(assigned)"}
                                        </Typography>
                                      </li>
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Select Device"
                                      placeholder="Device Name"
                                    />
                                  )}
                                />
                              </Box>

                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddDevice(room)}
                                sx={{ padding: 1.2, mt: 1 }}
                                disabled={isOccupy !== ""}
                              >
                                Add
                              </Button>
                            </Box>
                          </Collapse>
                        </Box>
                      ))
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Dialog open={openAddDialog} onClose={handleAddRoomClose}>
            <DialogContent>
              <DialogTitle sx={{ p: 0, mb: 4 }}>Add Room</DialogTitle>
              <TextField
                label="Room Name"
                fullWidth
                variant="outlined"
                value={newRoomName}
                onChange={(e) => handleCheckRoomName(e)}
                error={!!error}
                helperText={error}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleAddRoomClose}>Cancel</Button>
              <Button
                onClick={() => {
                  handleAddRoom();
                }}
                disabled={!!error}
              >
                Add
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </>
  );
};

export default Room;
